import React from 'react';
import styled from 'styled-components';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import ChevronIcon from 'src/assets/chevron-icon';

import useColors from 'hooks/use-colors';

export type AccordionBannerProps = {
  'data-testid'?: string;
  className?: string;
  graphic?: React.ReactNode;
  mainContent: React.ReactNode;
  toggleContent?: React.ReactNode;
  borderRadius?: string;
  marginBottom?: string;
  graphicWidth?: string;
  graphicFullHeight?: boolean;
};

export const AccordionBanner: React.FC<AccordionBannerProps> = ({
  'data-testid': dataTestId,
  className,
  graphic,
  mainContent,
  toggleContent,
  borderRadius,
  marginBottom,
  graphicWidth,
  graphicFullHeight,
}) => {
  const colors = useColors();

  return (
    <StyledAccordion
      className={className}
      data-testid={dataTestId}
      square
      $borderRadius={borderRadius}
      $marginBottom={marginBottom}
    >
      <StyledSummary expandIcon={toggleContent && <ChevronIcon height={10} width={10} fill={colors.green90} />}>
        {graphic && (
          <Graphic $fullHeight={graphicFullHeight} $width={graphicWidth}>
            {graphic}
          </Graphic>
        )}
        <MainContent $fullHeight={graphicFullHeight} $graphicWidth={graphicWidth}>
          {mainContent}
        </MainContent>
      </StyledSummary>
      {toggleContent && <AccordionDetails>{toggleContent}</AccordionDetails>}
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion)<{ $borderRadius?: string; $marginBottom?: string }>`
  &.MuiPaper-root {
    width: 100%;
    background-color: ${({ theme }) => theme.colors.green20};
    box-shadow: none;
    border: none;
    min-height: 68px;
    font-size: 13px;
    line-height: 18px;
    border-radius: ${({ $borderRadius }) => $borderRadius ?? '4px'};
    ${({ $marginBottom }) => $marginBottom && `margin-bottom: ${$marginBottom};`}
    z-index: 1;
    overflow: hidden;
  }

  &.MuiAccordion-root.Mui-expanded {
    margin-bottom: ${({ $marginBottom }) => $marginBottom ?? ''};
  }

  a {
    color: ${({ theme }) => theme.colors.green90};
    text-decoration: underline;
  }
`;

const StyledSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    margin: 0;
    padding: ${({ theme }) => `${theme.spaces[2]} ${theme.spaces[4]} ${theme.spaces[2]} ${theme.spaces[1]}`};
    height: 68px;
    gap: ${({ theme }) => theme.spaces[2]};
    position: relative;
  }
`;

const MainContent = styled.div<{ $fullHeight?: boolean; $graphicWidth?: string }>`
  display: flex;
  align-items: center;
  width: 100%;
  ${({ $fullHeight, $graphicWidth, theme }) =>
    $fullHeight
      ? // The graphic is lifted out of flow, so reserve its lane plus the standard content margin.
        `margin-left: calc(${$graphicWidth ?? '60px'} + ${theme.spaces[3]});`
      : `margin-left: ${theme.spaces[3]};`}
`;

const Graphic = styled.div<{ $fullHeight?: boolean; $width?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $width }) => $width ?? '60px'};
  ${({ $fullHeight, theme }) =>
    $fullHeight
      ? // Anchored to the summary root (not the flex line) so it covers top to bottom no matter
        // which item drives the summary height.
        `position: absolute;
         top: 0;
         bottom: 0;
         left: ${theme.spaces[2]};`
      : 'height: 60px;'}
`;
